import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Drawer from '@components/atoms/modals/drawer.tsx';
import { useGetPriorityQueueItem } from '@utils/api/back-office/priority-queue/use-get-priority-queue-item';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { PriorityBadge } from './priority-badge';
import { getErrorCategoryLabel } from '@utils/types/errors/error-categories';
import Button from '@components/atoms/ui-kit/button.tsx';
import { formatDate } from '@utils/date';
import { Flag, RefreshCcw, RotateCcw, Trash2, RefreshCw } from 'lucide-react';
import type { QueueStatus } from '@utils/types/back-office/priority-queue';
import type { SetPriorityTarget } from './set-priority-dialog';

type ItemAction = 'requeue' | 'retry' | 'delete';

interface QueueItemPanelProps {
  itemId: string | null;
  onClose: () => void;
  isMutating: boolean;
  onAction: (
    id: string,
    zoomId: string,
    status: QueueStatus,
    action: ItemAction
  ) => void;
  onSetPriority: (target: SetPriorityTarget) => void;
}

function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const { getColour } = useThemeContext();
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="text-xs font-medium"
        style={{ color: getColour('inactiveText') }}
      >
        {label}
      </span>
      <span className="text-sm" style={{ color: getColour('normalText') }}>
        {children}
      </span>
    </div>
  );
}

export function QueueItemPanel({
  itemId,
  onClose,
  isMutating,
  onAction,
  onSetPriority
}: QueueItemPanelProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();

  const { data: item, isFetching, refetch } = useGetPriorityQueueItem(itemId);

  const status = item?.status;
  const canSetPriority = status === 'queued';
  const canRequeue = status === 'processing';
  const canRetry = status === 'failed' || status === 'dead_letter';
  const canDelete =
    status === 'completed' ||
    status === 'dead_letter' ||
    status === 'failed' ||
    status === 'processing' ||
    status === 'queued';

  return (
    <Drawer
      isOpen={!!itemId}
      onClose={onClose}
      size="md"
      title={
        <span className="font-mono text-sm truncate">
          {item ? `#${item.id}` : '—'}
        </span>
      }
      caption={item?.zoom_id ?? undefined}
      headerExtra={
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center justify-center h-7 w-7 rounded-2xl hover:opacity-70 transition-opacity disabled:opacity-40"
          style={{ color: getColour('icon') }}
          aria-label={t('refresh')}
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      }
      footerActions={[
        <Button
          key="close"
          label={t('close')}
          variant="smoke"
          size="small"
          onClick={onClose}
        />
      ]}
    >
      {!item ? (
        <div
          className="flex items-center justify-center h-32 text-sm"
          style={{ color: getColour('inactiveText') }}
        >
          {isFetching ? t('loading') + '…' : '—'}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Status + badges */}
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={item.status} label={item.status} />
            <PriorityBadge priority={item.org_priority} />
          </div>

          {/* Actions */}
          <div
            className="flex flex-wrap gap-2 p-3 rounded-2xl border"
            style={{ borderColor: getColour('hairline') }}
          >
            <Button
              icon={<Flag size={13} />}
              label={t('set-priority')}
              size="small"
              variant="secondary"
              disabled={!canSetPriority || isMutating}
              onClick={() =>
                onSetPriority({
                  mode: 'single',
                  itemId: item.id,
                  currentPriority: item.org_priority,
                  zoomId: item.zoom_id
                })
              }
            />
            <Button
              icon={<RefreshCcw size={13} />}
              label={t('requeue')}
              size="small"
              variant="secondary"
              disabled={!canRequeue || isMutating}
              onClick={() =>
                onAction(item.id, item.zoom_id, item.status, 'requeue')
              }
            />
            <Button
              icon={<RotateCcw size={13} />}
              label={t('retry')}
              size="small"
              variant="secondary"
              disabled={!canRetry || isMutating}
              onClick={() =>
                onAction(item.id, item.zoom_id, item.status, 'retry')
              }
            />
            <Button
              icon={<Trash2 size={13} />}
              label={t('delete')}
              size="small"
              variant="danger"
              disabled={!canDelete || isMutating}
              onClick={() =>
                onAction(item.id, item.zoom_id, item.status, 'delete')
              }
            />
          </div>

          {/* Details */}
          <div
            className="grid grid-cols-2 gap-x-4 gap-y-4 p-4 rounded-2xl border"
            style={{ borderColor: getColour('hairline') }}
          >
            <Field label={t('id')}>
              <span className="font-mono break-all">{item.id}</span>
            </Field>
            <Field label={t('zoom-id')}>
              <span className="font-mono break-all">{item.zoom_id}</span>
            </Field>
            <Field label={t('created')}>
              {item.created_at ? formatDate(item.created_at) : '—'}
            </Field>
            <Field label={t('processing-started')}>
              {item.processing_started_at
                ? formatDate(item.processing_started_at)
                : '—'}
            </Field>
            {item.completed_at && (
              <Field label={t('completed-at') ?? 'Completed'}>
                {formatDate(item.completed_at)}
              </Field>
            )}
            <Field label={t('retry-count')}>
              {String(item.retry_count ?? 0)}
            </Field>
            <Field label={t('organization')}>
              {item.organization_id ? (
                <button
                  type="button"
                  className="text-left text-sm underline-offset-2 hover:underline"
                  style={{ color: getColour('activeText') }}
                  onClick={() =>
                    navigate(
                      `/back-office/organizations/${item.organization_id}`
                    )
                  }
                >
                  {item.organization_name ?? `#${item.organization_id}`}
                </button>
              ) : (
                <span style={{ color: getColour('inactiveText') }}>—</span>
              )}
            </Field>
          </div>

          {/* Error */}
          {item.error_message && (
            <div
              className="p-4 rounded-2xl border space-y-2"
              style={{ borderColor: getColour('hairline') }}
            >
              {item.error_category && (
                <span
                  className="block text-xs font-semibold uppercase"
                  style={{ color: getColour('activeText') }}
                >
                  {getErrorCategoryLabel(item.error_category)}
                </span>
              )}
              <p
                className="text-sm break-words whitespace-pre-wrap font-mono"
                style={{ color: getColour('normalText') }}
              >
                {item.error_message}
              </p>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
